import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode } from 'react';

export function Mark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`mark ${compact ? 'mark--compact' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 32 32" role="img">
        <path d="M7 24V8h9.2c5.1 0 8.3 2.6 8.3 7 0 4.3-3.2 7-8.3 7h-3.5v2H7Zm5.7-7h3.1c2 0 3-.7 3-2s-1-2-3-2h-3.1v4Z" />
      </svg>
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="spinner" role="status">
      <span aria-hidden="true" />
      {label}
    </span>
  );
}

export function Button({
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={`button ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: PropsWithChildren<{ tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info' }>) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function EmptyState({
  title,
  children,
  action,
}: PropsWithChildren<{ title: string; action?: ReactNode }>) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon" aria-hidden="true">
        ⌁
      </span>
      <h3>{title}</h3>
      <div className="muted">{children}</div>
      {action}
    </div>
  );
}

export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <div className="notice notice--bad" role="alert">
      <div>
        <strong>Couldn’t complete that action</strong>
        <p>{message}</p>
      </div>
      {onRetry ? (
        <Button className="button--quiet" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="skeleton" aria-label="Loading">
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: PropsWithChildren<{ label: string; hint?: string }>) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}
